import type { IntlShape } from 'react-intl';

import { fg } from '@confluence/feature-gating';
import { formatContentReportEpochTime } from '@confluence/macro-utils/entry-points/getRelativeTime.ts';

import type { ContentReportTableItem, SearchNode } from '../contentReportTableTypes.ts';
import { normalizeContentUrl } from '../contentReportTableUrlUtils.ts';

import { normalizeCreatorDisplayName } from './../contentReportTableUtils.ts';

const formatLastModified = (lastModified: string | null | undefined, intl: IntlShape): string => {
	if (!lastModified) {
		return '';
	}

	const timestamp = new Date(lastModified).getTime();
	if (Number.isNaN(timestamp)) {
		return lastModified;
	}

	return formatContentReportEpochTime(timestamp, intl);
};

export function mapSearchNodeToItem(
	node: SearchNode,
	intl: IntlShape,
): ContentReportTableItem | null {
	const normalizedUrl = normalizeContentUrl(node.url);

	if (!node.content || !normalizedUrl) {
		return null;
	}

	// eslint-disable-next-line confluence-feature-gating/no-new-feature-gates -- Existing gate retained during CCP-9677 lint-rule rollout; migration deferred.
	if (fg('cc_rewrite_macros_use_content_not_search_title')) {
		if (!node.content.title) {
			return null;
		}

		return {
			id: node.content.id,
			title: node.content.title,
			url: normalizedUrl,
			lastModified: formatLastModified(node.lastModified, intl),
			creatorAccountId: node.content.creatorId ?? null,
			creatorDisplayName: normalizeCreatorDisplayName(
				node.content.history?.createdBy?.displayName,
				intl,
			),
			commentCount: node.content.comments?.count ?? 0,
			likeCount: node.content.likes?.count ?? 0,
			spaceKey: node.content.space?.key ?? '',
		};
	}

	if (!node.title) {
		return null;
	}

	return {
		id: node.content.id,
		title: node.title,
		url: normalizedUrl,
		lastModified: formatLastModified(node.lastModified, intl),
		creatorAccountId: node.content.creatorId ?? null,
		creatorDisplayName: normalizeCreatorDisplayName(
			node.content.history?.createdBy?.displayName,
			intl,
		),
		commentCount: node.content.comments?.count ?? 0,
		likeCount: node.content.likes?.count ?? 0,
		spaceKey: node.content.space?.key ?? '',
	};
}
